import {
  BellRing,
  Blocks,
  LayoutDashboard,
  LogOut,
  Menu as MenuIcon,
  Monitor,
  Moon,
  Plug,
  Send,
  Settings,
  ShieldCheck,
  Sun,
  Waypoints,
  X,
} from 'lucide-react';
import { Suspense, useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router';
import { useAuth } from '@/auth/context';
import { MOCK } from '@/config';
import { cx } from '@/lib/cx';
import { useTheme, type ThemeChoice } from '@/lib/theme';
import { RouteErrorBoundary } from './ErrorBoundary';
import { Logo } from './Logo';
import { Spinner } from './ui';

const NAV = [
  { to: '/', label: 'Dashboard', icon: LayoutDashboard, end: true },
  { to: '/integrations', label: 'Integrations', icon: Blocks },
  { to: '/connections', label: 'Connections', icon: Plug },
  { to: '/updates', label: 'Updates', icon: Send },
  { to: '/alerts', label: 'Alerts', icon: BellRing },
  { to: '/destinations', label: 'Destinations', icon: Waypoints },
  { to: '/settings', label: 'Settings', icon: Settings },
] as const;

function NavItems({ onNavigate }: { onNavigate?: () => void }) {
  const { user } = useAuth();
  const { pathname } = useLocation();
  // KPI detail pages belong to the dashboard.
  const active = (to: string, isActive: boolean) => isActive || (to === '/' && pathname.startsWith('/kpi/'));
  const items = user?.role === 'admin' ? [...NAV, { to: '/admin', label: 'Admin', icon: ShieldCheck, end: false }] : NAV;
  return (
    <ul className="flex flex-col gap-0.5">
      {items.map(({ to, label, icon: Icon, ...rest }) => (
        <li key={to}>
          <NavLink
            to={to}
            end={'end' in rest ? rest.end : false}
            onClick={onNavigate}
            className={({ isActive }) =>
              cx(
                'flex h-10 items-center gap-3 rounded-control px-3 text-sm font-medium transition-colors',
                active(to, isActive) ? 'bg-card text-ink shadow-card ring-1 ring-border' : 'text-muted-ink hover:bg-muted hover:text-ink',
              )
            }
          >
            {({ isActive }) => (
              <>
                <Icon className={cx('size-[18px]', active(to, isActive) && 'text-ink')} strokeWidth={1.75} aria-hidden />
                {label}
                {active(to, isActive) && <span aria-hidden className="ml-auto size-1.5 rounded-full bg-yellow" />}
              </>
            )}
          </NavLink>
        </li>
      ))}
    </ul>
  );
}

function ThemeSwitch() {
  const { choice, setTheme } = useTheme();
  const opts: { v: ThemeChoice; label: string; icon: typeof Sun }[] = [
    { v: 'light', label: 'Light', icon: Sun },
    { v: 'system', label: 'System', icon: Monitor },
    { v: 'dark', label: 'Dark', icon: Moon },
  ];
  return (
    <div role="radiogroup" aria-label="Theme" className="inline-flex rounded-control border border-border bg-muted p-0.5">
      {opts.map(({ v, label, icon: Icon }) => (
        <button
          key={v}
          type="button"
          role="radio"
          aria-checked={choice === v}
          aria-label={`${label} theme`}
          title={`${label} theme`}
          onClick={() => setTheme(v)}
          className={cx(
            'inline-flex size-8 items-center justify-center rounded-[8px]',
            choice === v ? 'bg-card text-ink shadow-card' : 'text-muted-ink hover:text-ink',
          )}
        >
          <Icon className="size-4" aria-hidden />
        </button>
      ))}
    </div>
  );
}

function UserFooter() {
  const { user, signOut } = useAuth();
  return (
    <div className="flex flex-col gap-3 border-t border-border pt-4">
      {MOCK && (
        <p className="rounded-control border border-dashed border-border px-3 py-2 text-xs text-muted-ink">
          <span className="font-semibold text-ink">Mock API.</span> Data comes from MSW, not the server.
        </p>
      )}
      <div className="flex items-center justify-between gap-2">
        <ThemeSwitch />
        <button
          type="button"
          onClick={() => void signOut()}
          className="inline-flex h-9 items-center gap-2 rounded-control px-2 text-sm text-muted-ink hover:bg-muted hover:text-ink"
        >
          <LogOut className="size-4" aria-hidden />
          Sign out
        </button>
      </div>
      {user && (
        <div className="min-w-0 px-1 text-xs text-muted-ink">
          <p className="truncate font-medium text-ink">{user.name}</p>
          <p className="truncate">{user.email}</p>
        </div>
      )}
    </div>
  );
}

function MobileNav() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDialogElement>(null);
  const location = useLocation();
  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) el.showModal?.();
    if (!open && el.open) el.close?.();
  }, [open]);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Open menu"
        aria-expanded={open}
        className="inline-flex size-10 items-center justify-center rounded-control hover:bg-muted"
      >
        <MenuIcon className="size-5" aria-hidden />
      </button>
      {/* Clicking the backdrop closes the drawer; Escape is handled natively by <dialog>. */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions */}
      <dialog
        ref={ref}
        aria-label="Menu"
        onClose={() => setOpen(false)}
        onClick={(e) => e.target === ref.current && setOpen(false)}
        className="m-0 ml-auto h-dvh max-h-dvh w-[min(320px,88vw)] max-w-none border-l border-border bg-paper p-0 text-ink"
      >
        {open && (
          <nav aria-label="Main" className="flex h-full flex-col gap-6 p-4">
            <div className="flex items-center justify-between">
              <Logo className="h-7" />
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Close menu"
                className="inline-flex size-10 items-center justify-center rounded-control hover:bg-muted"
              >
                <X className="size-5" aria-hidden />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">
              <NavItems onNavigate={() => setOpen(false)} />
            </div>
            <UserFooter />
          </nav>
        )}
      </dialog>
    </>
  );
}

export function AppShell() {
  const location = useLocation();
  const mainRef = useRef<HTMLElement>(null);
  // Move focus to the page on navigation so screen readers announce the new screen.
  const first = useRef(true);
  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    mainRef.current?.focus({ preventScroll: true });
    window.scrollTo({ top: 0 });
  }, [location.pathname]);

  return (
    <div className="min-h-dvh lg:grid lg:grid-cols-[248px_1fr]">
      <a
        href="#main"
        className="sr-only z-50 rounded-control bg-yellow px-4 py-2 font-medium text-yellow-ink focus:not-sr-only focus:fixed focus:top-3 focus:left-3"
      >
        Skip to content
      </a>
      <aside className="sticky top-0 hidden h-dvh flex-col gap-8 border-r border-border px-4 py-6 lg:flex">
        <NavLink to="/" aria-label="MyDash dashboard" className="self-start rounded-control px-2">
          <Logo className="h-8" />
        </NavLink>
        <nav aria-label="Main" className="flex-1 overflow-y-auto">
          <NavItems />
        </nav>
        <UserFooter />
      </aside>
      <header className="sticky top-0 z-20 flex h-14 items-center justify-between border-b border-border bg-paper/90 px-4 backdrop-blur lg:hidden">
        <NavLink to="/" aria-label="MyDash dashboard" className="rounded-control">
          <Logo className="h-7" />
        </NavLink>
        <MobileNav />
      </header>
      <main id="main" ref={mainRef} tabIndex={-1} className="min-w-0 px-4 py-6 outline-none focus-visible:shadow-none sm:px-6 lg:px-10 lg:py-10">
        <div className="mx-auto max-w-[1240px]">
          <RouteErrorBoundary key={location.pathname}>
            <Suspense fallback={<Spinner className="mt-10" />}>
              <Outlet />
            </Suspense>
          </RouteErrorBoundary>
        </div>
      </main>
    </div>
  );
}
